/**
 * The rules a series, season or episode payload has to satisfy, in one place.
 *
 * The API runs the same rules, in the same order, with the same wording, from
 * microServices/modules/admin/videoShow/seriesValidation.ts. The two files are
 * deliberate mirrors: change one and change the other, or this form will start
 * accepting payloads the API rejects (or refusing ones it would have taken).
 * Both validate the request payload rather than form state, so there is one
 * shape to agree on — build the payload first, then hand it to these.
 *
 * Only what can be decided from the payload lives here. Anything needing the
 * database — does this series exist, does this season exist, is this episode
 * number already taken — is the API's answer to give, and comes back as an
 * error message from the request.
 */

const VALID = { valid: true };

const invalid = (message, seasonIndex) =>
  seasonIndex === undefined
    ? { valid: false, message }
    : { valid: false, message, seasonIndex };

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const asList = (value) => (Array.isArray(value) ? value : []);

const hasDuplicates = (values) => new Set(values).size !== values.length;

const isYesNo = (value) => value === "Y" || value === "N";

/**
 * Strict integer parse for values that are allowed to be 0 — season and episode
 * numbers, running times. Empty, absent and non-numeric all come back null.
 */
export const toIntegerOrNull = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return null;
  }

  const num = Number(value);
  return Number.isInteger(num) ? num : null;
};

/**
 * The series' star rating — 0 to 10, at most one decimal place, the shape
 * v_tv_show.star_rating is stored in. Blank, non-numeric, out of range and
 * finer than a tenth all come back null; telling "no rating given" apart from
 * "a rating that is wrong" is the caller's job, because the field is optional.
 */
export const toStarRatingOrNull = (value) => {
  if (isBlank(value)) {
    return null;
  }

  const num = Number(value);
  if (!Number.isFinite(num) || num < 0 || num > 10) {
    return null;
  }

  // One decimal place, checked by round trip rather than by string: 4.6 comes
  // back as itself, 4.55 comes back as 4.6 and is rejected. Rounding to a
  // tenth is exact enough for the range 0..10 — no value here has a binary
  // representation far enough off to survive the comparison by accident.
  return Math.round(num * 10) / 10 === num ? num : null;
};

/** "language, card image" — the parts of an image set that are still missing. */
const missingImageParts = (image) =>
  [
    isBlank(image?.lang_id) && "language",
    isBlank(image?.card_image) && "card image",
    isBlank(image?.banner_image) && "banner image",
  ]
    .filter(Boolean)
    .join(", ");

/**
 * Trailers and episode videos share a shape: a language and a finished upload.
 * `label` names the list in the message ("Series trailer", "Episode video").
 */
const validateVideoList = (list, label, duplicateMessage, seasonIndex) => {
  for (let i = 0; i < list.length; i++) {
    const entry = list[i];
    if (
      !entry ||
      isBlank(entry.lang) ||
      isBlank(entry.file_name) ||
      isBlank(entry.bunny_guid)
    ) {
      return invalid(
        `${label} #${i + 1}: select a language and finish the upload before saving`,
        seasonIndex
      );
    }
  }

  if (hasDuplicates(list.map((entry) => entry.lang))) {
    return invalid(duplicateMessage, seasonIndex);
  }

  return VALID;
};

const validateImageList = (list, label, duplicateMessage, seasonIndex) => {
  for (let i = 0; i < list.length; i++) {
    const missing = missingImageParts(list[i]);
    if (missing) {
      return invalid(`${label} #${i + 1}: ${missing} required`, seasonIndex);
    }
  }

  if (hasDuplicates(list.map((image) => image.lang_id))) {
    return invalid(duplicateMessage, seasonIndex);
  }

  return VALID;
};

const validateSeason = (season, seasonNo, seasonIndex) => {
  if (isBlank(season?.season_title)) {
    return invalid(`Season ${seasonNo}: season title is required`, seasonIndex);
  }

  return VALID;
};

export const validateSeriesPayload = (payload) => {
  if (isBlank(payload?.tv_show_name)) {
    return invalid("Series name is required");
  }
  if (asList(payload?.lang_list).length === 0) {
    return invalid("At least one language is required");
  }
  if (asList(payload?.genre_list).length === 0) {
    return invalid("At least one genre is required");
  }
  if (isBlank(payload?.rating_id)) {
    return invalid("A rating is required");
  }
  // The star rating, unlike rating_id above it, is optional: a series with no
  // rating is a series the API answers with rating: null. Only a value that is
  // actually there has to be a number in range.
  if (
    !isBlank(payload?.star_rating) &&
    toStarRatingOrNull(payload?.star_rating) === null
  ) {
    return invalid("Rating must be a number between 0 and 10");
  }
  if (!isYesNo(payload?.is_subscription_required)) {
    return invalid("Subscription required must be set");
  }
  if (!isYesNo(payload?.tv_show_is_active)) {
    return invalid("Series status must be set");
  }

  const images = validateImageList(
    asList(payload?.images),
    "Series image set",
    "Duplicate languages in series images"
  );
  if (!images.valid) return images;

  // Season numbers first, as a set: ms_tv_show_seasons is unique on
  // (season_no, tv_show_id) and seasons are written one at a time without a
  // transaction, so a repeat fails partway with the earlier ones already saved.
  const seasons = asList(payload?.seasons_list);
  const seasonNumbers = seasons.map((season) =>
    toIntegerOrNull(season?.season_no)
  );

  for (let i = 0; i < seasonNumbers.length; i++) {
    if (seasonNumbers[i] === null) {
      return invalid(`Season entry #${i + 1}: a numeric season number is required`, i);
    }
  }

  const duplicateIndex = seasonNumbers.findIndex(
    (seasonNo, index) => seasonNumbers.indexOf(seasonNo) !== index
  );
  if (duplicateIndex !== -1) {
    return invalid(
      `Season ${seasonNumbers[duplicateIndex]} is listed twice — each season needs its own number`,
      duplicateIndex
    );
  }

  for (let i = 0; i < seasons.length; i++) {
    const season = validateSeason(seasons[i], seasonNumbers[i], i);
    if (!season.valid) return season;
  }

  return VALID;
};

export const validateEpisodePayload = (payload) => {
  if (toIntegerOrNull(payload?.tv_show_id) === null) {
    return invalid("A valid series id is required");
  }

  const seasonNo = toIntegerOrNull(payload?.season_no);
  if (seasonNo === null || seasonNo < 0) {
    return invalid("A valid season number is required");
  }

  const episodeNo = toIntegerOrNull(payload?.episode_no);
  if (episodeNo === null || episodeNo < 0) {
    return invalid("A valid episode number is required");
  }

  if (isBlank(payload?.episode_title)) {
    return invalid("Episode title is required");
  }
  if (isBlank(payload?.episode_summary)) {
    return invalid("Episode summary is required");
  }

  const runtime = toIntegerOrNull(payload?.episode_runtime);
  if (runtime === null || runtime < 0) {
    return invalid("Running time must be a non-negative number");
  }

  const endtime = toIntegerOrNull(payload?.episode_endtime);
  if (endtime === null || endtime < 0) {
    return invalid("End time must be a non-negative number");
  }

  if (isBlank(payload?.release_date)) {
    return invalid("Release date is required");
  }
  if (!isYesNo(payload?.episode_is_active)) {
    return invalid("Episode status must be set");
  }

  const videos = validateVideoList(
    asList(payload?.video_files),
    "Episode video",
    "Duplicate languages in episode videos"
  );
  if (!videos.valid) return videos;

  const images = validateImageList(
    asList(payload?.images),
    "Episode image set",
    "Duplicate languages in episode images"
  );
  if (!images.valid) return images;

  return VALID;
};
